import { useState } from 'react';
import { Button, DateRangePicker, FilterChip } from '@indipia/ui-core';
import { DATE_PRESETS, type DatePreset } from '../../config/datePreset';
import type { BasicInfoPartner } from '../../types/worklog.types';
import { toDash } from './feedModel';
import { PartnerFilterPopover } from './PartnerFilterPopover';

/**
 * 공사현황 피드 조회조건 바 — 기간(직접 설정 + 프리셋 칩) · 작성자 · 정렬.
 * 공사현황 전체 화면과 나의 현장 '공사현황' 탭이 같은 자리에서 같은 모양으로 쓴다(001 V2.6 · 004 E).
 */
export function WorklogFeedToolbar({
  range, preset, customRange, onPresetChange, onCustomRangeChange, holidays,
  partners, partnersError, selectedPartners, onPartnersChange, sortDesc, onToggleSort,
}: {
  /** 현재 유효한 기간(직접 설정이 있으면 그것, 없으면 프리셋) */
  range: { from: Date; to: Date };
  preset: DatePreset;
  customRange: { from: string; to: string } | null;
  onPresetChange: (preset: DatePreset) => void;
  onCustomRangeChange: (range: { from: string; to: string } | null) => void;
  holidays: Set<string>;
  /** null = 로딩 중 */
  partners: BasicInfoPartner[] | null;
  partnersError: boolean;
  selectedPartners: BasicInfoPartner[];
  onPartnersChange: (list: BasicInfoPartner[]) => void;
  sortDesc: boolean;
  onToggleSort: () => void;
}) {
  const [partnerPopOpen, setPartnerPopOpen] = useState(false);
  // 목업 실측: 버튼 라벨은 선택 인원과 무관하게 "작성자"(선택 있으면 "작성자 N") — 이름은 드롭다운 안에서만 체크 표시.
  const partnerLabel = selectedPartners.length > 0 ? `작성자 ${selectedPartners.length}` : '작성자';

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-6 py-3">
      <div className="flex flex-wrap items-center gap-2">
        {/* 기간 직접 설정 — 옆의 프리셋 칩보다 우선한다(active 로 어느 쪽이 이겼는지 표시) */}
        <DateRangePicker
          value={{ start: range.from, end: range.to }}
          onChange={({ start, end }) => {
            if (start && end) onCustomRangeChange({ from: toDash(start), to: toDash(end) });
          }}
          active={!!customRange}
          holidays={holidays}
          presets={false}
          aria-label="기간 직접 설정"
        />

        {/* 기간 프리셋 (공통 — 작업요청·현장보고와 통일) */}
        {DATE_PRESETS.map(([key, label]) => (
          <FilterChip
            key={key}
            active={!customRange && preset === key}
            onClick={() => {
              onCustomRangeChange(null);
              onPresetChange(key);
            }}
          >
            {label}
          </FilterChip>
        ))}
      </div>

      <div className="flex items-center gap-2">
        {/* 작성자 필터 */}
        <div className="relative">
          <button
            onClick={() => setPartnerPopOpen((v) => !v)}
            className={[
              'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-body-md font-medium transition-colors',
              selectedPartners.length
                ? 'bg-group-subtle text-group'
                : 'border border-border bg-surface-raised text-text-secondary hover:bg-surface-muted',
            ].join(' ')}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
              <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" />
            </svg>
            {partnerLabel}
          </button>
          {partnerPopOpen && (
            <PartnerFilterPopover
              selected={selectedPartners}
              partners={partners}
              error={partnersError}
              onClose={() => setPartnerPopOpen(false)}
              onApply={(list) => {
                onPartnersChange(list);
                setPartnerPopOpen(false);
              }}
            />
          )}
        </div>

        {/* 정렬 — 화면기획서 실측: 2지선다 Segmented가 아니라 아이콘·라벨이 바뀌는 단일 토글 버튼(WorkOrderListPage와 동일) */}
        <Button variant="etc" size="md" onClick={onToggleSort} aria-label="정렬">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
            <path d="M12 5v14" />
            {sortDesc ? <path d="M5 12l7-7 7 7" /> : <path d="M19 12l-7 7-7-7" />}
          </svg>
          {sortDesc ? '최신순' : '과거순'}
        </Button>
      </div>
    </div>
  );
}
